import React, { ComponentType, useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";

import { useForm } from "antd/es/form/Form";
import { Location as LocationType } from "history";
import { useHistory } from "react-router-dom";

import { EntityType } from "@mapmycustomers/shared/enum";

import AccessReasonExposer from "@app/component/AccessReasonExposer";
import EmailSubscriptionStatusTag from "@app/component/EmailSubscriptionStatusTag";
import FrequencyPanel from "@app/component/frequency/FrequencyPanel";
import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import Groups from "@app/component/preview/components/Groups";
import Location from "@app/component/preview/components/Location";
import NoAccess from "@app/component/preview/components/NoAccess";
import Tabs from "@app/component/preview/components/Tabs";
import RecordActionsBar from "@app/component/RecordCard/components/RecordActionsBar";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import { EnterPinLocationModePayload } from "@app/scene/map/store/pinLocation/actions";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { EntityWithLocationProps } from "@app/types/map/EntityWithLocationProps";
import eventEmitter from "@app/util/eventEmitter";
import companyFieldModel, { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { companyLayoutModel } from "@app/util/layout/impl";

import Emails from "../../components/Emails";
import useTabs from "../../util/useTabs";
import Activities from "../Activities";
import Details from "../Details";
import DetailsFormValues from "../Details/DetailsFormValues";
import Files from "../Files";
import Notes from "../Notes";
import RelationshipsContainer from "../RelationshipsContainer";
import {
  createCompanyNote,
  setActiveTab,
  setActivityAutoScrolling,
  subscribeCompanyEmail,
  unsubscribeCompanyEmail,
  updateCompany,
  updateCompanyFrequency,
} from "../store/actions";
import CompanyRecordData from "../store/CompanyRecordData";
import {
  doesNeedToScrollToActivity,
  getActiveTab,
  isCompanyUpdateLoading,
} from "../store/selectors";

import styles from "./Content.module.scss";
import useHandleAddressChange from "./useHandleAddressChange";

type ContentProps = {
  formChanged: boolean;
  isMapMode: boolean;
  locationEditComponent?: ComponentType<EntityWithLocationProps>;
  onEnterPinLocationMode?: (payload: EnterPinLocationModePayload) => void;
  onFormChange: (isChanged: boolean) => void;
  onHide: () => void;
  onNotesChange?: (notesChanged: boolean) => void;
  recordData: CompanyRecordData;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const Content: React.FC<ContentProps> = ({
  activeTab,
  companyUpdateLoading,
  doesNeedToScrollToActivity,
  formChanged,
  isMapMode,
  isReadOnlyMember,
  locationEditComponent,
  onActiveTabChange,
  onActivityAutoScrollingChange,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onEnterPinLocationMode,
  onFormChange,
  onHide,
  onNoteAdded,
  onNotesChange,
  onUpdateCompany,
  onUpdateCompanyFrequency,
  onUpdateCompanySuccess,
  recordData,
}) => {
  const [detailsForm] = useForm<DetailsFormValues>();
  const {
    activities,
    activitiesCompletedByType,
    activitiesTotal,
    childCompanies,
    company,
    deals,
    files,
    notes,
    parentCompany,
    people,
    routes,
    totalDeals,
    totalPeople,
  } = recordData;
  const history = useHistory();
  const activeTabRef = useRef<HTMLDivElement | null>(null);

  const { defaultTabRedirect, defaultTabsOrder, handleTabsChange, handleTabsOrderChange } = useTabs(
    {
      activeTab,
      entity: company,
      onActiveTabChange,
    }
  );

  useLayoutEffect(() => {
    if (doesNeedToScrollToActivity && activeTabRef.current) {
      activeTabRef.current.scrollIntoView({ behavior: "smooth" });
    }
    onActivityAutoScrollingChange(false);
  }, [doesNeedToScrollToActivity, onActivityAutoScrollingChange]);

  const handleAddressChange = useHandleAddressChange(
    company,
    detailsForm,
    isMapMode,
    onUpdateCompany,
    onUpdateCompanySuccess
  );

  const handleFrequencyUpdate = useCallback(() => {
    if (company) {
      onUpdateCompanyFrequency({
        companyId: company.id,
      });
    }
  }, [company, onUpdateCompanyFrequency]);

  const handleShowActivities = useCallback(() => {
    activeTabRef.current?.scrollIntoView({ behavior: "smooth" });
    history.push(RecordPaneTab.ACTIVITIES);
  }, [activeTabRef, history]);

  const handleActiveTabRefChange = useCallback((newActiveTabRef: HTMLDivElement | null) => {
    activeTabRef.current = newActiveTabRef;
  }, []);

  const schema = useMemo(
    () => (company ? companyLayoutModel.getSchemaFor(company) : undefined),
    [company]
  );

  const filesField = companyFieldModel.getByName(CompanyFieldName.FILES);
  const notesField = companyFieldModel.getByName(CompanyFieldName.NOTES);
  const fileSchemaField = useMemo(
    () => schema?.find((schemaField) => schemaField.field === CompanyFieldName.FILES),
    [schema]
  );

  const handleLeave = useCallback(() => {
    detailsForm.resetFields();
    onFormChange(false);
  }, [detailsForm, onFormChange]);

  const shouldShowPrompt = (location: LocationType) =>
    formChanged && location.pathname !== `/${RecordPaneTab.DETAILS}`;

  const handleSave = useCallback(() => {
    eventEmitter.emit("saveCompanyChanges");
  }, []);

  if (!company) {
    return <NoAccess />;
  }

  return (
    <section className={styles.details}>
      <div className={styles.detailsTopContainer}>
        <div className={styles.columnWrapper}>
          <AccessReasonExposer entity={company} />
          <RecordActionsBar
            className={styles.actions}
            companies={childCompanies}
            entity={company}
            onEmailSubscribe={onEmailSubscribe}
            onEmailUnsubscribe={onEmailUnsubscribe}
            onNoteAdded={onNoteAdded}
          />
          <EmailSubscriptionStatusTag
            className={styles.emailSubscriptionStatusTag}
            emailSubscribed={company.subscribed}
          />
          <FrequencyPanel
            entity={company}
            groups={company.groups}
            onUpdate={handleFrequencyUpdate}
            onViewActivityHistory={handleShowActivities}
          />
          <Groups entity={company} entityType={EntityType.COMPANY} />
        </div>
        <Location
          addressLoading={companyUpdateLoading}
          entity={company}
          formChanged={formChanged}
          isMapMode={isMapMode}
          isReadOnlyMember={isReadOnlyMember}
          locationEditComponent={locationEditComponent}
          onAddressChange={handleAddressChange}
        />
      </div>
      <Tabs
        activeTab={activeTab ?? defaultTabRedirect}
        className={styles.tabs}
        defaultTabsOrder={defaultTabsOrder}
        onActiveTabRefChange={handleActiveTabRefChange}
        onChange={handleTabsChange}
        onOrderChange={handleTabsOrderChange}
        tabs={[
          {
            children: (
              <Details
                company={company}
                companyUpdateLoading={companyUpdateLoading}
                form={detailsForm}
                formChanged={formChanged}
                isMapMode={isMapMode}
                onAddressChange={handleAddressChange}
                onEnterPinLocationMode={onEnterPinLocationMode}
                onFormChange={onFormChange}
                onHide={onHide}
              />
            ),
            tab: RecordPaneTab.DETAILS,
          },
          {
            children: (
              <Activities
                activities={activities}
                activitiesCompletedByType={activitiesCompletedByType}
                activitiesTotal={activitiesTotal}
                company={company}
              />
            ),
            tab: RecordPaneTab.ACTIVITIES,
          },
          {
            children: (
              <RelationshipsContainer
                childCompanies={childCompanies}
                company={company}
                deals={deals}
                parentCompany={parentCompany}
                people={people}
                routes={routes}
                totalDeals={totalDeals}
                totalPeople={totalPeople}
              />
            ),
            tab: RecordPaneTab.RELATIONSHIPS,
          },
          {
            children: (
              <Notes
                allowedUserIds={company.userIds}
                notes={notes}
                onNotesChange={onNotesChange}
                uneditable={!notesField?.isEditable || isReadOnlyMember}
                unreadable={!notesField?.isReadable}
              />
            ),
            tab: RecordPaneTab.NOTES,
          },
          {
            children: (
              <Files
                accessStatus={company.accessStatus}
                files={files}
                required={fileSchemaField?.required}
                uneditable={!filesField?.isEditable || isReadOnlyMember}
                unreadable={!filesField?.isReadable}
              />
            ),
            tab: RecordPaneTab.FILES,
          },
          {
            children: <Emails entity={company} />,
            tab: RecordPaneTab.EMAILS,
          },
        ]}
      />
      <InterceptNavigationModal
        onLeave={handleLeave}
        onSave={handleSave}
        shouldCloseOnLeave
        shouldShowPrompt={shouldShowPrompt}
      />
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeTab: getActiveTab(state),
  companyUpdateLoading: isCompanyUpdateLoading(state),
  doesNeedToScrollToActivity: doesNeedToScrollToActivity(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  onActiveTabChange: setActiveTab,
  onActivityAutoScrollingChange: setActivityAutoScrolling,
  onEmailSubscribe: subscribeCompanyEmail.request,
  onEmailUnsubscribe: unsubscribeCompanyEmail.request,
  onNoteAdded: createCompanyNote.success,
  onUpdateCompany: updateCompany.request,
  onUpdateCompanyFrequency: updateCompanyFrequency.request,
  onUpdateCompanySuccess: updateCompany.success,
};

export default connect(mapStateToProps, mapDispatchToProps)(Content);
